import { useState, useCallback } from 'react';
import {
  pushErrorFromAPI,
  SdkErrorResponse,
} from 'src/utilities/notifications';
import { IErrorResponse } from 'src/utilities/shared-types';
import { Report } from 'src/types/Report';
import { getReportsAPI } from 'src/components/enterprise/features/Reporting/ManageReportsPage/components/ReportsWrapper/api/getReportsAPI';
import { usePolling } from 'src/utilities/usePolling';
import { hasPendingReports } from 'src/components/enterprise/features/Reporting/ManageReportsPage/components/ReportsWrapper/hooks/useGetReports.utils';

export interface UseGetReportsProps {
  corpId: string;
}

export interface UseGetReportsReturn {
  reports: Report[];
  isLoading: boolean;
  error?: IErrorResponse | SdkErrorResponse;
  fetch: () => Promise<void>;
}

export function useGetReports({
  corpId,
}: UseGetReportsProps): UseGetReportsReturn {
  const [reports, setReports] = useState<Report[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<IErrorResponse | SdkErrorResponse>();

  const fetchCallback = useCallback(async () => {
    const fetchedReports = await getReportsAPI(corpId);
    setReports(fetchedReports);
    return fetchedReports;
  }, [corpId]);

  const handleBackgroundError = useCallback((e: unknown) => {
    const errorObject = e as IErrorResponse | SdkErrorResponse;
    setError(errorObject);
    pushErrorFromAPI(errorObject);
  }, []);

  const POLLING_INTERVAL_MS = 5000;
  const POLLING_MAX_ATTEMPTS = 100;

  const { startPolling } = usePolling(fetchCallback, hasPendingReports, {
    pollingIntervalMs: POLLING_INTERVAL_MS,
    maxAttempts: POLLING_MAX_ATTEMPTS,
    onError: handleBackgroundError,
  });

  const fetch = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(undefined);
      await startPolling();
    } catch (e) {
      handleBackgroundError(e);
    } finally {
      setIsLoading(false);
    }
  }, [startPolling, handleBackgroundError]);

  return {
    reports,
    isLoading,
    error,
    fetch,
  };
}
